/**
 * Google Analytics (GA4) integration.
 * Set VITE_GA_MEASUREMENT_ID (e.g. G-XXXXXXXXXX) to enable.
 */

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

function loadScript(): Promise<void> {
  if (!MEASUREMENT_ID || typeof document === 'undefined') return Promise.resolve();
  if (document.querySelector('script[src*="googletagmanager.com"]')) return Promise.resolve();

  return new Promise((resolve) => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
}

/**
 * Initialize Google Analytics. Call once on app load.
 * No-op if VITE_GA_MEASUREMENT_ID is not set.
 */
export function initAnalytics(): void {
  if (!MEASUREMENT_ID) return;
  loadScript().then(() => {
    window.gtag?.('config', MEASUREMENT_ID, {
      send_page_view: false, // we send page_view ourselves on route change
    });
  });
}

/**
 * Send a page view for the current path.
 * Call on initial load and whenever the route changes (SPA).
 */
export function trackPageView(path: string, title?: string): void {
  if (!MEASUREMENT_ID || !window.gtag) return;
  window.gtag('config', MEASUREMENT_ID, {
    page_path: path,
    page_title: title ?? document.title,
  });
}

/**
 * Track a custom event (e.g. 'pixel_placed', 'archive_viewed').
 */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!MEASUREMENT_ID || !window.gtag) return;
  window.gtag('event', name, params);
}
